import React from 'react';
import { Link } from 'react-router-dom';
import { Package, Shield, DollarSign, ArrowRight, CheckCircle, Clock, Users, TrendingUp } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const STEPS = [
  { num: '01', title: 'Submit Your Items', description: 'Contact us with details about your collection. Include photos, descriptions, and your valuation expectations.', icon: Package },
  { num: '02', title: 'Expert Evaluation', description: 'Our team reviews your items for authenticity, condition, and market value. We provide a transparent assessment.', icon: Shield },
  { num: '03', title: 'We List & Sell', description: 'Your items are professionally photographed, listed, and marketed to our network of active buyers.', icon: TrendingUp },
  { num: '04', title: 'You Get Paid', description: 'When your items sell, you receive your payout minus our consignment fee. No upfront costs.', icon: DollarSign },
];

const FAQS = [
  { q: 'What items are eligible for consignment?', a: 'We accept coins, precious metals, sports cards, trading cards, and high-value collectibles. Items should typically be valued at $250 or more.' },
  { q: 'What is the consignment fee?', a: 'Our standard consignment fee is 15% of the final sale price. Volume consignors may qualify for reduced rates.' },
  { q: 'How long does the process take?', a: 'Most items are listed within 5-7 business days of receiving them. Sale times vary by item type and market demand.' },
  { q: 'What if my item doesn\'t sell?', a: 'If your item doesn\'t sell within the agreed consignment period (typically 90 days), you can choose to relist, adjust pricing, or have the item returned.' },
  { q: 'How do I get paid?', a: 'Payouts are processed within 5 business days of a completed sale via direct deposit or check.' },
];

export default function ConsignmentPage() {
  return (
    <div className="min-h-screen bg-surface-base">
      <Header />

      {/* Hero */}
      <section className="py-20 bg-surface-card border-b border-border-subtle">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 border border-gold/20 mb-6">
            <Package className="w-4 h-4 text-gold" />
            <span className="text-sm text-gold font-medium">Consignment Services</span>
          </div>
          <h1 className="font-clash text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mb-6">
            Let Us Sell <span className="gold-text">For You</span>
          </h1>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto mb-10">
            Have valuable items but don't want to deal with listing, photographing, and negotiating?
            Our consignment service handles everything while you earn top dollar.
          </p>
          <Link to="/register" className="btn-gold px-8 py-4 inline-flex items-center gap-2 uppercase tracking-wider text-sm">
            Start Consignment <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-surface-base">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.2em] uppercase text-gold font-semibold mb-3">Process</p>
            <h2 className="font-clash text-3xl sm:text-4xl font-medium text-white">How Consignment Works</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map(step => (
              <div key={step.num} className="p-6 bg-surface-card border border-border-subtle relative">
                <div className="absolute -top-3 -left-3 w-10 h-10 bg-gold text-black font-clash font-bold text-sm flex items-center justify-center">
                  {step.num}
                </div>
                <step.icon className="w-8 h-8 text-gold mb-4 mt-2" />
                <h3 className="font-clash text-lg font-medium text-white mb-2">{step.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-surface-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-clash text-3xl sm:text-4xl font-medium text-white mb-6">
                Why Consign With CoinHustle?
              </h2>
              <div className="space-y-4">
                {[
                  'Professional photography and listing creation',
                  'Access to thousands of active, verified buyers',
                  'Expert pricing to maximize your returns',
                  'Secure handling and insurance coverage',
                  'No upfront costs — we only earn when you do',
                  'Transparent reporting and real-time status updates',
                ].map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <p className="text-zinc-300">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-8 bg-surface-base border border-gold/20">
              <h3 className="font-clash text-2xl font-semibold text-white mb-4">Quick Stats</h3>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="font-clash text-3xl font-bold text-gold">93%</p>
                  <p className="text-sm text-zinc-400">Items sold within 30 days</p>
                </div>
                <div>
                  <p className="font-clash text-3xl font-bold text-gold">15%</p>
                  <p className="text-sm text-zinc-400">Standard consignment fee</p>
                </div>
                <div>
                  <p className="font-clash text-3xl font-bold text-gold">5-7</p>
                  <p className="text-sm text-zinc-400">Days to list your items</p>
                </div>
                <div>
                  <p className="font-clash text-3xl font-bold text-gold">$250+</p>
                  <p className="text-sm text-zinc-400">Minimum item value</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-surface-base">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-clash text-3xl font-medium text-white mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {FAQS.map(faq => (
              <div key={faq.q} className="p-6 bg-surface-card border border-border-subtle">
                <h4 className="font-medium text-white mb-2">{faq.q}</h4>
                <p className="text-sm text-zinc-400 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
